import React, { memo } from 'react';
import { MessageMeta } from '@/components/Messenger/shared/messages/MessageMeta';

export type DmMessageBubbleVariant = 'own' | 'peer';

/** `attachmentBare` — только файлы/чипы без текста: без фона и скругления «бабла», только карточки. */
export type DmMessageBubbleSurface = 'bubble' | 'attachmentBare';

export type DmMessageBubbleProps = {
  variant: DmMessageBubbleVariant;
  /** По умолчанию полноценный пузырь; для attachment-only — без оболочки бабла. */
  surface?: DmMessageBubbleSurface;
  /**
   * Группа / агент: полная строка шапки (имеет приоритет над `peerSenderName`).
   * Для `variant="own"` не используется.
   */
  peerHeaderContent?: React.ReactNode;
  /**
   * Группа / ЛС: имя отправителя над пузырём (только `peer`).
   * Для `variant="own"` не используется.
   */
  peerSenderName?: string | null;
  /**
   * У padding у бабла peer (например imageOnly edge-to-edge).
   * Для `variant="own"` класс не вешается — исходящий composite уже без внешнего padding.
   */
  noPadding?: boolean;
  /**
   * Резерв: в DM мета живёт внутри BubbleShell / углу на медиа.
   * Если задано — рендерится под стеком peer (как в legacy IncomingDmPeerBubble).
   */
  timeLabel?: string;
  /** @deprecated */
  assistantFooterSlot?: React.ReactNode;
  children: React.ReactNode;
};

/**
 * Единая оболочка DM-пузыря: исходящий (`own`) и входящий (`peer`).
 * Внутренний контент (BubbleShell, imageOnly-слот и т.д.) передаётся в `children`.
 */
function DmMessageBubbleInner({
  variant,
  surface = 'bubble',
  peerHeaderContent,
  peerSenderName,
  noPadding = false,
  timeLabel,
  assistantFooterSlot,
  children,
}: DmMessageBubbleProps): React.ReactNode {
  const stackTail = (
    <>
      {assistantFooterSlot}
      {timeLabel ? (
        <MessageMeta timeLabel={timeLabel} showReadReceipts={false} isOwn={false} />
      ) : null}
    </>
  );

  if (variant === 'own') {
    if (surface === 'attachmentBare') {
      return (
        <div className="chatAssistant__dmAttachmentBare chatAssistant__dmAttachmentBare_own">
          <div className="chatAssistant__dmIncomingStack">
            {children}
            {stackTail}
          </div>
        </div>
      );
    }
    return (
      <div className="chatAssistant__message chatAssistant__message_user chatAssistant__message_user_composite chatAssistant__message_dm">
        <div className="chatAssistant__dmIncomingStack">
          {children}
          {stackTail}
        </div>
      </div>
    );
  }

  const name = peerSenderName?.trim() ?? '';
  const useUnifiedHeader = peerHeaderContent != null && peerHeaderContent !== false;
  const hasHeader = useUnifiedHeader || Boolean(name);

  const authorHeader = hasHeader ? (
    <div
      className="chatAssistant__dmPeerAuthorHeaderRow"
      title={!useUnifiedHeader && name ? name : undefined}
    >
      {useUnifiedHeader ? (
        <div className="chatAssistant__dmPeerAuthorHeaderSingle">{peerHeaderContent}</div>
      ) : (
        <span className="chatAssistant__dmPeerAuthorHeaderName">{peerSenderName}</span>
      )}
    </div>
  ) : null;

  if (surface === 'attachmentBare') {
    return (
      <div className="chatAssistant__dmAttachmentBare chatAssistant__dmAttachmentBare_peer">
        <div className="chatAssistant__dmIncomingStack">
          {authorHeader}
          {children}
          {stackTail}
        </div>
      </div>
    );
  }

  return (
    <div
      className={[
        'chatAssistant__message',
        'chatAssistant__message_assistant',
        'chatAssistant__message_dm',
        noPadding ? 'chatAssistant__message_dm_noPadding' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="chatAssistant__dmIncomingStack">
        {authorHeader}
        {children}
        {stackTail}
      </div>
    </div>
  );
}

export const DmMessageBubble = memo(DmMessageBubbleInner);
